import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { components } from '@/api/schema';

export type LicenceStatus = components['schemas']['LicenceStatus'];

export const licenceKey = ['licence'] as const;

/** État de la licence ou de l'abonnement (relu toutes les 5 minutes). */
export function useLicence() {
  return useQuery({
    queryKey: licenceKey,
    queryFn: () => apiFetch<LicenceStatus>('/licence', { noRetry: true }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Modules « sur demande » (en attendant l'état de la licence, qui donne le catalogue). */
const ON_DEMAND_FALLBACK = ['JOURNAUX', 'PRESTATAIRES', 'IA', 'GDT'];

/** Le module est-il ouvert ? `modules` absent = tous les modules, sauf les modules « sur
 * demande », ouverts seulement si l'éditeur les a activés pour la clinique. */
export function moduleAllowed(status: LicenceStatus | undefined, module?: string): boolean {
  if (!module) return true;
  const onDemand = status
    ? Boolean(status.catalogue.find((m) => m.code === module)?.sur_demande)
    : ON_DEMAND_FALLBACK.includes(module);
  if (onDemand) {
    const extra = status?.modules_sur_demande;
    return extra === null || (extra ?? []).includes(module);
  }
  if (!status?.modules) return true;
  return status.modules.includes(module);
}

/** Jours restants à partir desquels la fin de l'abonnement est annoncée. */
const SUBSCRIPTION_WARNING_DAYS = 15;

export function licenceNotice(
  status: LicenceStatus | undefined,
): { color: string; title: string; text: string; locked: boolean } | null {
  if (!status) return null;
  if (status.etat === 'lecture_seule') {
    return { color: 'red', title: 'Lecture seule', text: status.message, locked: true };
  }
  if (status.etat === 'essai') {
    return { color: 'blue', title: 'Essai gratuit', text: status.message, locked: false };
  }
  if (
    status.code === 'abonnement' &&
    status.jours_restants != null &&
    status.jours_restants <= SUBSCRIPTION_WARNING_DAYS
  ) {
    const days = status.jours_restants;
    return {
      color: 'orange',
      title: 'Abonnement bientôt terminé',
      text: `${status.message} Il reste ${days} jour${days > 1 ? 's' : ''} : pensez à le renouveler auprès de l'éditeur.`,
      locked: false,
    };
  }
  return null;
}
